import { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Plus, Download, Package, Terminal, BookOpen, Pencil, Trash2 } from 'lucide-react';
import { useTenantScope } from '../context/TenantScopeContext';
import { useAuth } from '../context/AuthContext';
import { ColumnDef, DraggableTh, useColumnPrefs } from '../components/ColumnPicker';
import EntraIntegrationPanel from '../components/EntraIntegrationPanel';
import EdrIntegrationPanel from '../components/EdrIntegrationPanel';
import AthenaTenantMappingPanel from '../components/AthenaTenantMappingPanel';
import SangforEppConsolesPanel from '../components/SangforEppConsolesPanel';
import LecsIntegrationPanel from '../components/LecsIntegrationPanel';
import VmwareIntegrationPanel from '../components/VmwareIntegrationPanel';
import CustomerPublicIpsPanel from '../components/CustomerPublicIpsPanel';
import TenantSelect from '../components/TenantSelect';

interface Site {
  id: number;
  tenant_id: number;
  name: string;
  address: string | null;
}

interface Broker {
  id: number;
  site_id: number;
  name: string;
  status: 'pending' | 'active' | 'revoked';
  cert_expires_at: string | null;
  last_seen_at: string | null;
  last_data_at: string | null;
  offline_minutes: string | null;
  silent_minutes: string | null;
}

// Il broker è considerato online se ha inviato un segnale di presenza negli ultimi 10 minuti
// (l'heartbeat arriva ogni minuto): stessa soglia usata dal monitoraggio Data Quality.
function isOnline(broker: Broker): boolean {
  return broker.offline_minutes !== null && Number(broker.offline_minutes) <= 10;
}

function formatAgo(minutes: string | null): string {
  if (minutes === null) return 'never';
  const value = Math.round(Number(minutes));
  if (value < 1) return 'just now';
  if (value < 60) return `${value} min ago`;
  const hours = Math.floor(value / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

interface Provisioning {
  certPem: string;
  keyPem: string;
  caCertPem: string;
}

interface ApplianceImage {
  name: string;
  size: number;
  modifiedAt: string;
  md5: string | null;
}

function formatSize(bytes: number): string {
  return `${(bytes / 1024 ** 3).toFixed(1)} GB`;
}

// Etichetta per piattaforma al posto del nome file (resta nel tooltip).
const APPLIANCE_LABELS: Record<string, string> = {
  ova: 'VMware',
  vma: 'Sangfor HCI',
  qcow2: 'KVM (Proxmox, NexaVM, oVirt)',
  conf: 'KVM VM config',
};

function applianceLabel(name: string): string {
  return APPLIANCE_LABELS[name.split('.').pop()!.toLowerCase()] || name;
}

const BROKER_COLUMNS: ColumnDef[] = [
  { key: 'broker', label: 'Broker', defaultVisible: true },
  { key: 'status', label: 'Status', defaultVisible: true },
  { key: 'connection', label: 'Connection', defaultVisible: true },
  { key: 'last_data', label: 'Last data', defaultVisible: true },
  { key: 'cert_expiry', label: 'Certificate expiry', defaultVisible: true },
  { key: 'installer', label: 'Installer', defaultVisible: true },
];

export default function TenantDetailPage() {
  const { orderedDefs: brokerDefs, moveColumn: moveBrokerColumn } = useColumnPrefs('soc.columns.tenant_brokers', BROKER_COLUMNS);
  const { selectedTenantId, selectedTenantName } = useTenantScope();
  const { user, isAdmin, canEdit } = useAuth();
  const [sites, setSites] = useState<Site[]>([]);
  const [brokersBySite, setBrokersBySite] = useState<Record<number, Broker[]>>({});
  const [showSiteForm, setShowSiteForm] = useState(false);
  const [siteName, setSiteName] = useState('');
  const [brokerNameBySite, setBrokerNameBySite] = useState<Record<number, string>>({});
  const [lastProvisioning, setLastProvisioning] = useState<{ brokerId: number; data: Provisioning } | null>(null);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [appliances, setAppliances] = useState<ApplianceImage[]>([]);

  useEffect(() => {
    if (!canEdit) return;
    axios.get('/api/brokers/appliances').then(res => setAppliances(res.data.data)).catch(() => setAppliances([]));
  }, [canEdit]);

  // Le immagini pesano diversi GB: niente blob in memoria come per il pacchetto, il browser
  // scarica direttamente da un link firmato valido 10 minuti (vedi routes/brokers.ts).
  async function downloadAppliance(name: string) {
    setError(null);
    try {
      const res = await axios.post(`/api/brokers/appliances/${encodeURIComponent(name)}/link`);
      window.location.href = res.data.data.url;
    } catch (err: any) {
      setError(err.response?.data?.error || 'Could not prepare the image download');
    }
  }

  async function loadSites() {
    if (!selectedTenantId) return;
    const res = await axios.get('/api/sites', { params: { tenantId: selectedTenantId } });
    const loadedSites: Site[] = res.data.data;
    setSites(loadedSites);
    const brokerEntries = await Promise.all(
      loadedSites.map(s => axios.get('/api/brokers', { params: { siteId: s.id } }).then(r => [s.id, r.data.data] as const))
    );
    setBrokersBySite(Object.fromEntries(brokerEntries));
  }

  // Ricarica periodica per tenere aggiornato lo stato di connessione dei broker.
  useEffect(() => {
    loadSites();
    const interval = setInterval(loadSites, 30_000);
    return () => clearInterval(interval);
  }, [selectedTenantId]);

  async function handleCreateSite(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await axios.post('/api/sites', { tenantId: selectedTenantId, name: siteName });
      setSiteName(''); setShowSiteForm(false);
      loadSites();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error while creating the site');
    }
  }

  async function handleCreateBroker(siteId: number) {
    setError(null);
    try {
      const res = await axios.post('/api/brokers', { siteId, name: brokerNameBySite[siteId] || 'Broker' });
      setBrokerNameBySite(prev => ({ ...prev, [siteId]: '' }));
      setLastProvisioning({ brokerId: res.data.data.broker.id, data: res.data.data.provisioning });
      loadSites();
    } catch (err: any) {
      setError(err.response?.data?.error || "Error while provisioning the broker");
    }
  }

  // Rinomina/elimina (2026-09-24): prima un broker col nome sbagliato restava per sempre.
  async function renameBroker(b: Broker) {
    const name = window.prompt('New broker name', b.name)?.trim();
    if (!name || name === b.name) return;
    setError(null);
    try {
      await axios.patch(`/api/brokers/${b.id}`, { name });
      loadSites();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Could not rename the broker');
    }
  }

  async function deleteBroker(b: Broker) {
    const stillTalking = b.status === 'active' && b.last_seen_at && (Date.now() - new Date(b.last_seen_at).getTime()) < 10 * 60 * 1000;
    const warning = stillTalking
      ? `"${b.name}" is ACTIVE and was seen in the last 10 minutes: an installed collector is still sending logs through it.\n\n`
        + 'Deleting it drops the connection at its next reconnect and removes its log sources from the console (collected logs are kept). '
      : `Delete broker "${b.name}"?\n\nIts certificate stops being accepted and its log sources disappear from the console (collected logs are kept). `;
    if (!window.confirm(warning + 'This cannot be undone.')) return;
    setError(null);
    try {
      await axios.delete(`/api/brokers/${b.id}`);
      loadSites();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Could not delete the broker');
    }
  }

  // Solo siti vuoti: il backend rifiuta (409) se ci sono ancora broker o IP pubblici legati.
  async function deleteSite(site: Site) {
    if (!window.confirm(`Delete site "${site.name}"?\n\nThis cannot be undone.`)) return;
    setError(null);
    try {
      await axios.delete(`/api/sites/${site.id}`);
      loadSites();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Could not delete the site');
    }
  }

  function downloadFile(filename: string, content: string) {
    const blob = new Blob([content], { type: 'application/x-pem-file' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  }

  // Ogni download emette un nuovo certificato: l'eventuale installazione precedente di questo
  // broker smette di essere autorizzata, quindi va sostituita con il pacchetto appena scaricato.
  async function downloadInstaller(broker: Broker) {
    if (!window.confirm(
      `Download the installer for "${broker.name}"?\n\n` +
      'A new certificate will be issued: any existing installation of this broker will stop ' +
      'being accepted until you deploy this new package.'
    )) return;

    setDownloadingId(broker.id);
    setError(null);
    try {
      const res = await axios.post(`/api/brokers/${broker.id}/package`, {}, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `soc-broker-${broker.name.replace(/[^a-zA-Z0-9._-]/g, '-')}.tar.gz`;
      a.click();
      URL.revokeObjectURL(url);
      loadSites();
    } catch {
      setError('Could not build the installer package. Check that the platform host is set in Platform Settings.');
    } finally {
      setDownloadingId(null);
    }
  }

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <h1 className="text-xl font-semibold text-gray-900">Sites & Brokers</h1>
        <TenantSelect />
      </div>
      {!selectedTenantId && (
        <div className="card p-8 text-center">
          <p className="text-gray-500 text-sm">Select a specific tenant from the “Viewing” menu in the sidebar to manage its sites and brokers.</p>
        </div>
      )}
      {selectedTenantId && (
      <>
      {error && <div className="mb-4 text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</div>}

      <CustomerPublicIpsPanel tenantId={selectedTenantId} sites={sites.map(s => ({ id: s.id, name: s.name }))} />
      <EntraIntegrationPanel tenantId={selectedTenantId} />
      <EdrIntegrationPanel tenantId={selectedTenantId} />
      {/* Sangfor Athena XDR push sospesa (29/10/2026): solo platform admin. */}
      {user?.scopeLevel === 'platform' && user?.role === 'admin' && (
        <AthenaTenantMappingPanel tenantId={selectedTenantId} />
      )}
      {/* Console Sangfor Athena EPP (syslog via broker): un EDR come gli altri, visibile a tutti;
          il relay verso un altro tenant resta ai platform admin (routes/eppManagers.ts). */}
      <SangforEppConsolesPanel tenantId={selectedTenantId} />
      {/* LECS è configurata per partner (chiave API e dispositivi di tutti i suoi clienti): la
          gestisce chi opera a livello partner o platform, non un utente del singolo tenant. */}
      {user?.scopeLevel !== 'tenant' && <LecsIntegrationPanel tenantId={selectedTenantId} />}
      <VmwareIntegrationPanel tenantId={selectedTenantId} />

      {lastProvisioning && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4 text-sm">
          <p className="font-medium text-amber-800 mb-2">
            Certificate for broker #{lastProvisioning.brokerId} generated — download it now, it won't be retrievable again afterwards.
          </p>
          <div className="flex gap-2">
            <button onClick={() => downloadFile('broker-cert.pem', lastProvisioning.data.certPem)} className="flex items-center gap-1 bg-amber-600 hover:bg-amber-700 text-white rounded-lg px-3 py-1.5">
              <Download size={14} /> Certificate
            </button>
            <button onClick={() => downloadFile('broker-key.pem', lastProvisioning.data.keyPem)} className="flex items-center gap-1 bg-amber-600 hover:bg-amber-700 text-white rounded-lg px-3 py-1.5">
              <Download size={14} /> Private key
            </button>
            <button onClick={() => downloadFile('ca-cert.pem', lastProvisioning.data.caCertPem)} className="flex items-center gap-1 bg-amber-600 hover:bg-amber-700 text-white rounded-lg px-3 py-1.5">
              <Download size={14} /> CA certificate
            </button>
          </div>
        </div>
      )}

      {canEdit && appliances.length > 0 && (
        <div className="card p-4 mb-4">
          <h2 className="font-medium text-gray-900 mb-1">Broker download</h2>
          <p className="text-xs text-gray-500 mb-3">Ready-to-use VM for the customer's hypervisor. The broker is not inside yet: after importing the VM, install the broker package from the Download button of its broker below. <Link to="/guides/broker-install-image" className="text-primary-700 underline">Installation guide</Link></p>
          <div className="flex flex-wrap gap-2">
            {appliances.map(a => (
              <button key={a.name} onClick={() => downloadAppliance(a.name)}
                title={`${a.name} · updated ${new Date(a.modifiedAt).toLocaleString('en-GB')}`}
                className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium text-primary-700 bg-primary-50 hover:bg-primary-100">
                <Download size={13} /> {applianceLabel(a.name)} · {formatSize(a.size)}
              </button>
            ))}
          </div>
          {appliances.some(a => a.md5) && (
            <table className="text-xs mt-3">
              <tbody>
                {appliances.filter(a => a.md5).map(a => (
                  <tr key={a.name}>
                    <td className="pr-3 py-0.5 text-gray-500">{applianceLabel(a.name)}</td>
                    <td className="pr-2 py-0.5 text-gray-400">MD5</td>
                    <td className="py-0.5 font-mono text-gray-700 select-all">{a.md5}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {canEdit && (
        <button onClick={() => setShowSiteForm(v => !v)} className="btn-primary flex items-center gap-1 mb-4">
          <Plus size={16} /> New site
        </button>
      )}

      {showSiteForm && (
        <form onSubmit={handleCreateSite} className="card p-4 mb-4 flex gap-3 items-end">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Site name</label>
            <input value={siteName} onChange={e => setSiteName(e.target.value)} required className="input-field" />
          </div>
          <button type="submit" className="btn-primary">Create</button>
        </form>
      )}

      <div className="space-y-4">
        {sites.map(site => (
          <div key={site.id} className="card p-4">
            <div className="flex items-center gap-2 mb-2">
              <h2 className="font-medium text-gray-900">{site.name}</h2>
              {isAdmin && (
                (brokersBySite[site.id] || []).length === 0
                  ? <button onClick={() => deleteSite(site)} title="Delete site" className="text-gray-300 hover:text-red-600"><Trash2 size={14} /></button>
                  : <span title="Delete its brokers first to delete the site" className="text-gray-200 cursor-not-allowed"><Trash2 size={14} /></span>
              )}
            </div>
            <table className="w-full text-sm mb-3">
              <thead>
                <tr>
                  {brokerDefs.map(d => (
                    <DraggableTh key={d.key} colKey={d.key} onMove={moveBrokerColumn}
                      className={`table-header ${d.key === 'installer' ? 'text-right' : ''}`}>
                      {d.label}
                    </DraggableTh>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(brokersBySite[site.id] || []).map(b => (
                  <tr key={b.id} className="border-t border-gray-100">
                    {brokerDefs.map(d => (
                      <td key={d.key} className={`table-cell ${d.key === 'last_data' || d.key === 'cert_expiry' ? 'text-gray-500' : ''} ${d.key === 'installer' ? 'text-right' : ''}`}
                        title={d.key === 'last_data' ? (b.last_data_at ? new Date(b.last_data_at).toLocaleString('en-GB') : '') : undefined}>
                        {d.key === 'broker' && (
                          <span className="inline-flex items-center gap-1.5 group">
                            <span className="font-medium text-gray-900">{b.name}</span>
                            {canEdit && <button onClick={() => renameBroker(b)} title="Rename broker" className="text-gray-300 hover:text-primary-600"><Pencil size={13} /></button>}
                            {isAdmin && <button onClick={() => deleteBroker(b)} title="Delete broker" className="text-gray-300 hover:text-red-600"><Trash2 size={13} /></button>}
                          </span>
                        )}
                        {d.key === 'status' && (
                          <span className={`badge ${
                            b.status === 'active' ? 'bg-green-100 text-green-700' :
                            b.status === 'pending' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
                          }`}>{b.status}</span>
                        )}
                        {d.key === 'connection' && (
                          b.status !== 'active' ? (
                            <span className="text-gray-400">—</span>
                          ) : isOnline(b) ? (
                            <span className="inline-flex items-center gap-1.5 badge bg-green-100 text-green-700"
                              title={b.last_seen_at ? `Last seen ${new Date(b.last_seen_at).toLocaleString('en-GB')}` : ''}>
                              <span className="w-1.5 h-1.5 rounded-full bg-green-500" /> Online
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 badge bg-red-100 text-red-700"
                              title={b.last_seen_at ? `Last seen ${new Date(b.last_seen_at).toLocaleString('en-GB')}` : 'Never connected'}>
                              <span className="w-1.5 h-1.5 rounded-full bg-red-500" /> Offline · {formatAgo(b.offline_minutes)}
                            </span>
                          )
                        )}
                        {d.key === 'last_data' && formatAgo(b.silent_minutes)}
                        {d.key === 'cert_expiry' && (b.cert_expires_at ? new Date(b.cert_expires_at).toLocaleDateString('en-GB') : '—')}
                        {d.key === 'installer' && (
                          <span className="inline-flex items-center gap-1 justify-end">
                            <Link to={`/guides/broker-install?brokerId=${b.id}`}
                              title="Step-by-step installation guide, with the commands already filled in for this broker"
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium text-gray-600 bg-gray-50 hover:bg-gray-100">
                              <BookOpen size={13} /> Guide
                            </Link>
                            {b.status === 'active' && (
                              <Link to={`/broker-console/${b.id}`}
                                title="Open broker console (status, live logs, update)"
                                className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium text-gray-600 bg-gray-50 hover:bg-gray-100">
                                <Terminal size={13} /> Console
                              </Link>
                            )}
                            <button onClick={() => downloadInstaller(b)} disabled={downloadingId === b.id}
                              title="Download the ready-to-run installer package for the on-site VM"
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium text-primary-700 bg-primary-50 hover:bg-primary-100 disabled:opacity-50">
                              <Package size={13} /> {downloadingId === b.id ? 'Preparing…' : 'Download'}
                            </button>
                          </span>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
                {(brokersBySite[site.id] || []).length === 0 && (
                  <tr><td className="table-cell text-gray-400" colSpan={6}>No broker registered yet</td></tr>
                )}
              </tbody>
            </table>

            {canEdit && (
            <div className="flex gap-2 items-center">
              <input
                placeholder="Broker name"
                value={brokerNameBySite[site.id] || ''}
                onChange={e => setBrokerNameBySite(prev => ({ ...prev, [site.id]: e.target.value }))}
                className="input-field max-w-xs"
              />
              <button onClick={() => handleCreateBroker(site.id)} className="btn-secondary flex items-center gap-1 whitespace-nowrap">
                <Plus size={14} /> Provision broker
              </button>
            </div>
            )}
          </div>
        ))}
        {sites.length === 0 && <p className="text-gray-400 text-sm">No site created yet for this tenant.</p>}
      </div>
      </>
      )}
    </div>
  );
}
